import { plainToClass } from "class-transformer";
import { LineStringFeature } from "../../../../../utils/models/LineStringFeature";
import type { Route } from "../../../../../utils/models/Route";
import { remapDestinationsAfterReversal, type StageDestination } from "../../../services/stage-destinations";
import type { UndoCommand, CommandContext } from "../types";

/**
 * Reverse a single route's path (markers + linestrings) in place.
 */
export function reverseRouteInPlace(route: Route): void {
  route.markers.reverse();
  route.lineStrings = route.lineStrings.map(lineString => {
    const instance = plainToClass(LineStringFeature, lineString);
    return instance.reverse();
  }).reverse();
  route.updated_at = new Date();
}

/**
 * Reverse a single route AND keep stage-destination (POI/waypoint/highlight)
 * attachments intact — including the trail-start destination. If the
 * reversed route carried the trail start or a day's stage end, reversing it
 * swaps which of its endpoints sits there, so we re-key `stageDestinations`/
 * `trailStartDestination` onto whichever anchor now sits on them — otherwise
 * the render-time prune (which validates attachment by coordinate proximity)
 * would drop them.
 *
 * Used by both the initial reverse action AND this command's undo/redo, so
 * a destination that got moved by the forward reverse is moved back by the
 * next one — matching ReverseItineraryCommand's self-inverse pattern.
 */
export function reverseRouteStateInPlace(
  state: {
    routes: { [routeKey: string]: Route };
    stageDestinations?: { [dayNumber: number]: StageDestination };
    trailStartDestination?: StageDestination | null;
  },
  routeKey: string
): void {
  const route = state.routes[routeKey];
  if (!route) return;
  reverseRouteInPlace(route);

  const hasStageDestinations = state.stageDestinations && Object.keys(state.stageDestinations).length > 0;
  if (hasStageDestinations || state.trailStartDestination) {
    const remapped = remapDestinationsAfterReversal(
      state.routes,
      state.stageDestinations || {},
      state.trailStartDestination || null
    );
    state.stageDestinations = remapped.stageDestinations;
    state.trailStartDestination = remapped.trailStartDestination;
  }
}

/**
 * Command for reversing a route. Self-inverse: undo and redo both
 * reverse the route again, restoring the original order.
 * Stores only the route key (~100 bytes).
 */
export class ReverseRouteCommand implements UndoCommand {
  readonly type = 'REVERSE_ROUTE';
  readonly routeKey: string;

  constructor(routeKey: string) {
    this.routeKey = routeKey;
  }

  undo(ctx: CommandContext): void {
    reverseRouteStateInPlace(ctx.state, this.routeKey);
  }

  redo(ctx: CommandContext): void {
    reverseRouteStateInPlace(ctx.state, this.routeKey);
  }
}
